/* Print page right column — the desktop's collapsible boxes (PrinterPanel / PrintSettingsBox /
 * PrintJobsBox / Ink levels), in the Brightivity kit. Order: Printers → Print jobs → Print settings → Ink levels. */
import { helperDownloadUrl, browserOs } from '../../api/bridge.js';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Button, Icon, Modal, Select, Spinner } from '../../ui/index.jsx';
import { cancelJob, dismissJob, listMine } from '../../api/school.js';
import { CRITICAL_INK, LOW_INK, inkInfo, sourceValue, tone as paperTone } from './printRules.js';
import { IP_HELP_GROUPS, IP_HELP_GUIDES, IP_HELP_MODELS } from './ipHelpModels.js';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/index.js';

const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private window */ } },
};

/** Box open state, saved per box (desktop usePersistedOpen); `transient` changes are not saved (nesting folds). */
export function usePersistedOpen(key, def = true) {
  const [open, setOpenState] = useState(() => { const v = store.get(key); return v == null ? def : v === '1'; });
  const setOpen = useCallback((v) => setOpenState((o) => { const n = typeof v === 'function' ? v(o) : v; store.set(key, n ? '1' : '0'); return n; }), [key]);
  return [open, setOpen, setOpenState];
}

export function PanelBox({ id, title, tone, open, setOpen, actions, summary, children, testid, tour, attrs }) {
  return (
    <section className={`bv-pbox${open ? ' is-open' : ''}`} id={id} data-testid={testid} data-tour={tour} {...attrs}>
      <div className="bv-pbox__head">
        <button type="button" className="bv-pbox__toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span className="bv-pbox__title">{title}</span>
          {tone && <span className={`bv-tdot bv-tdot--${tone}`} aria-hidden="true" />}
        </button>
        {actions}
        <button type="button" className="bv-pbox__chev" aria-label={open ? `Fold ${title}` : `Open ${title}`} onClick={() => setOpen(!open)}><Icon name="down" size={18} /></button>
      </div>
      {open ? <div className="bv-pbox__body">{children}</div> : summary ? <div className="bv-pbox__summary">{summary}</div> : null}
    </section>
  );
}

/* ------------------------------------------------------------------ Printers */
const ago = (t) => {
  if (!t) return '';
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return i18n.t('bv:time.justNow');
  if (m < 60) return i18n.t('bv:time.minutesAgo', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return i18n.t('bv:time.hoursAgo', { count: h });
  return i18n.t('bv:time.daysAgo', { count: Math.floor(h / 24) });
};
const freshTone = (t) => { const m = (Date.now() - t) / 60000; return m < 10 ? 'ok' : m < 25 ? 'warn' : 'error'; };
const paperTitle = (dev) => `${dev.name || dev.type || '—'}${parseFloat(dev.widthIn) > 0 ? ` · ${parseFloat(dev.widthIn)}"` : ''}`;
const TONE_OF = { green: 'ok', yellow: 'warn', coral: 'error', blue: 'info', idle: 'idle' };

/** Inline disclosure, never a native select (desktop PaperSourcePicker). */
export function PaperPicker({ sources, value, onChange, disabled, loading, placeholder }) {
  const [open, setOpen] = useState(false);
  const selected = sources.find((d) => sourceValue(d) === value) || null;
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  return (
    <div className={`bv-paper${open ? ' is-open' : ''}`}>
      <button type="button" id="paper-select" className="bv-paper__current" disabled={disabled || sources.length === 0}
        aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="paper-select">
        {loading ? <Spinner label="Loading paper sources" /> : <span className={`bv-tdot bv-tdot--${selected ? paperTone(selected.status) : 'idle'}`} />}
        <span className="bv-paper__text">
          {selected ? (
            <>
              <span className="bv-paper__name">{paperTitle(selected)}</span>
              {(selected.paper || paperTone(selected.status) === 'error') && <span className="bv-paper__type">{paperTone(selected.status) === 'error' ? `${selected.status || 'No paper'}` : selected.paper}</span>}
            </>
          ) : <span className="bv-paper__placeholder">{loading ? '⏳ Loading paper sources...' : placeholder}</span>}
        </span>
        {sources.length > 1 && <Icon name="down" size={16} className="bv-paper__caret" />}
      </button>
      {open && (
        <div className="bv-paper__list" role="listbox">
          {sources.map((dev, i) => {
            const v = sourceValue(dev);
            const active = v === value;
            return (
              <button type="button" role="option" aria-selected={active} key={`${v}-${i}`} className={`bv-paper__option${active ? ' is-active' : ''}`}
                data-is-roll={String(dev.isRoll === true)} onClick={() => { onChange(v); setOpen(false); }}>
                <span className={`bv-tdot bv-tdot--${paperTone(dev.status)}`} />
                <span className="bv-paper__text">
                  <span className="bv-paper__name">{paperTitle(dev)}</span>
                  <span className="bv-paper__type">{dev.paper || dev.status || ''}</span>
                </span>
                {active && <Icon name="check" size={16} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const PANEL_ICONS = { menu: '☰', info: 'ⓘ', settings: '⚙', network: '⊕', wifi: '📶', search: '🔍' };
function StepText({ text }) {
  return String(text || '').split(/(\[[a-z]+\]|\*[^*]+\*)/g).filter(Boolean).map((part, i) => {
    const icon = part.match(/^\[([a-z]+)\]$/);
    if (icon && PANEL_ICONS[icon[1]]) return <span key={i} className={`bv-panelicon bv-panelicon--${icon[1]}`}>{icon[1] === 'search' ? <Icon name="search" size={13} /> : PANEL_ICONS[icon[1]]}</span>;
    const bold = part.match(/^\*([^*]+)\*$/);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}
export function IpHelpModal({ open, onClose }) {
  const { t } = useTranslation('bv');
  const [model, setModel] = useState('');
  useEffect(() => { if (!open) setModel(''); }, [open]);
  const picked = IP_HELP_MODELS.find((m) => m.id === model) || null;
  const guideKey = picked ? IP_HELP_GUIDES[picked.guide] || IP_HELP_GUIDES.default : null;
  const guide = guideKey ? t(`print.iphelp.guides.${guideKey}`, { returnObjects: true }) : null;
  return (
    <Modal open={open} onClose={onClose} title={t('print.iphelp.title')} width={520}>
      <div className="bv-label">{t('print.iphelp.yourModel')}</div>
      <div className="bv-iphelp__groups">
        {IP_HELP_GROUPS.map((g) => (
          <div key={g.label}>
            <div className="bv-small bv-muted">{t(g.label)}</div>
            <div className="bv-iphelp__row">{g.models.map((m) => <button type="button" key={m.id} className={`bv-iphelp__chip${model === m.id ? ' is-active' : ''}`} onClick={() => setModel(m.id)}>{m.label}</button>)}</div>
          </div>
        ))}
      </div>
      {guide ? (
        <div className="bv-iphelp__guide">
          <ol className="bv-iphelp__steps">
            {(Array.isArray(guide.steps) ? guide.steps : []).map((s, i) => <li key={i}><span className="bv-iphelp__n">{i + 1}</span><span><StepText text={s} /></span></li>)}
            <li className="is-final"><span className="bv-iphelp__n"><Icon name="check" size={13} /></span><span><StepText text={t('print.iphelp.finalStep')} /></span></li>
          </ol>
          <div className="bv-iphelp__look"><span>{t('print.iphelp.lookFor')}</span><span className="bv-iphelp__sample"><b>{t('print.iphelp.ipAddress')}</b> 192.168.1.25</span></div>
          {guide.alt && <p className="bv-small"><b>{t('print.iphelp.anotherWay')}</b> <StepText text={guide.alt} /></p>}
          {guide.source && <p className="bv-small bv-muted">{guide.source}</p>}
        </div>
      ) : <p className="bv-small bv-muted">{t('print.iphelp.pickModel')}</p>}
    </Modal>
  );
}

function SwitchModal({ open, onClose, printers, selected, onSelect, onAddNew }) {
  const { t } = useTranslation('bv');
  return (
    <Modal open={open} onClose={onClose} title={t('print.switchPrinter')} width={460}>
      <div className="bv-switch">
        {printers.map((p) => (
          <button type="button" key={p.ip} className={`bv-switch__row${p.ip === selected ? ' is-active' : ''}`} data-ip={p.ip} onClick={() => { onSelect(p.ip); onClose(); }}>
            <span className="bv-switch__icon"><Icon name="print" size={18} /></span>
            <span className="bv-switch__text"><b>{p.model || p.ip}</b><span>{p.ip}</span></span>
            {p.ip === selected && <Icon name="check" size={18} />}
          </button>
        ))}
        {!printers.length && <p className="bv-small bv-muted">{t('print.noPrintersYet')}</p>}
      </div>
      <div className="bv-modal-actions"><Button icon="plus" onClick={() => { onClose(); onAddNew(); }}>{t('print.addRemove')}</Button></div>
    </Modal>
  );
}

const validIp = (ip) => /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.test(ip) && ip.split('.').every((n) => Number(n) <= 255);

function AddForm({ online, finding, onFind, onAddIp, saved, onRemove, showSaved, notify }) {
  const { t } = useTranslation('bv');
  const [ip, setIp] = useState('');
  const [probing, setProbing] = useState(false);
  const [help, setHelp] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const submit = async () => {
    const v = ip.trim();
    if (!v) { notify(t('print.ip.enter')); return; }
    if (!validIp(v)) { notify(t('print.ip.invalid'), 'coral'); return; }
    setProbing(true);
    const ok = await onAddIp(v);
    setProbing(false);
    if (ok) setIp('');
  };
  return (
    <div className="bv-addform" data-testid="add-printer">
      <Button variant="blue" block icon={finding ? undefined : 'search'} disabled={finding || !online} onClick={onFind} id="discover-btn">
        {finding ? <><Spinner label={t('print.ip.searching')} /> {t('print.ip.searchingNetwork')}</> : t('print.ip.find')}
      </Button>
      <div className="bv-addform__or"><span>{t('print.ip.orAdd')}</span></div>
      <div className="bv-addform__ip">
        <input className="bv-input" placeholder="192.168.1.100" aria-label={t('print.ip.aria')} value={ip} disabled={!online || probing}
          onChange={(e) => setIp(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
        <button type="button" className="bv-roundbtn" title={t('print.ip.findAt')} aria-label={t('print.ip.findAt')} disabled={!online || probing} onClick={submit}>
          {probing ? <Spinner label={t('print.ip.looking')} /> : <Icon name="search" size={18} />}
        </button>
        <button type="button" className="bv-roundbtn" title={t('print.ip.how')} aria-label={t('print.ip.how')} onClick={() => setHelp(true)}>?</button>
      </div>
      {!online && <p className="bv-small bv-muted">{t('print.ip.offline')}</p>}
      {showSaved && saved.length > 0 && (
        <div className="bv-addform__saved">
          <div className="bv-label">{t('print.yourPrinters')}</div>
          {saved.map((p) => (
            <div key={p.ip} className="bv-addform__printer">
              <span><b>{p.model || p.ip}</b><span className="bv-muted">{p.ip}</span></span>
              <button type="button" className="bv-roundbtn" title={t('print.removePrinter')} aria-label={t('print.removeNamed', { name: p.model || p.ip })} disabled={!online} onClick={() => setConfirm(p)}><Icon name="trash" size={16} /></button>
            </div>
          ))}
        </div>
      )}
      <IpHelpModal open={help} onClose={() => setHelp(false)} />
      <Modal open={!!confirm} title={t('print.removePrinter')} onClose={() => setConfirm(null)}>
        <p className="bv-muted">{confirm && t('print.removeSure', { name: confirm.model || t('print.thisPrinter'), ip: confirm.ip })}</p>
        <div className="bv-modal-actions">
          <Button onClick={() => setConfirm(null)}>{t('common.cancel')}</Button>
          <Button variant="coral" icon="trash" onClick={() => { const p = confirm; setConfirm(null); onRemove(p); }}>{t('common.remove')}</Button>
        </div>
      </Modal>
    </div>
  );
}

/** The live status card (desktop #printer-live-status). */
function LiveCard({ pill, loading }) {
  if (loading && !pill) return <div className="bv-livecard bv-livecard--idle"><Spinner label={i18n.t('bv:print.checkingPrinter')} /><span>{i18n.t('bv:print.checkingPrinterDots')}</span></div>;
  if (!pill) return null;
  const icon = pill.tone === 'green' ? '🟢' : pill.tone === 'yellow' ? '🟡' : pill.tone === 'coral' ? '🔴' : pill.tone === 'blue' ? '🖨️' : '⚪';
  return (
    <div className={`bv-livecard bv-livecard--${pill.tone}`} id="printer-live-status" data-testid="printer-status">
      <span aria-hidden="true">{icon}</span>
      <span className="bv-livecard__text"><b>{pill.text === 'Ready to print' ? 'Printer healthy' : pill.text}</b>{(pill.details || []).map((d, i) => <span key={i}>{d}</span>)}</span>
    </div>
  );
}

export function PrintersBox({
  open, setOpen, devices, device, setDevice, helperState, pickerShown, localDevice, printers, printer, selectPrinter, info, infoAt, loadingInfo, refreshing, onRefresh,
  sources, source, setSource, pill, finding, onFind, onAddIp, onRemovePrinter, notify,
}) {
  const { t } = useTranslation('bv');
  const [adding, setAdding] = useState(false);
  const [switching, setSwitching] = useState(false);
  const online = !!(device && device.online);
  const cur = sources.find((d) => sourceValue(d) === source) || null;
  const showAdd = !printers.length || adding;
  const tone = pill ? TONE_OF[pill.tone] || 'idle' : loadingInfo ? 'idle' : info && info.ok ? 'ok' : 'idle';
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(t); }, []);
  const summary = printer ? (
    <button type="button" className="bv-pbox__sumbtn" onClick={() => setOpen(true)}>
      <b>{printer.model || printer.ip}</b>
      <span>{loadingInfo && !cur ? t('print.loadingSources') : cur ? [cur.name || cur.type, parseFloat(cur.widthIn) > 0 ? `${parseFloat(cur.widthIn)}"` : null, cur.paper].filter(Boolean).join(' · ') : t('print.selectSource')}</span>
    </button>
  ) : (
    <button type="button" className="bv-linkbtn" onClick={() => { setOpen(true); if (printers.length) setSwitching(true); else setAdding(true); }}>{printers.length ? t('print.selectPrinterPlus') : t('print.addPrinterPlus')}</button>
  );
  return (
    <PanelBox id="printers-box" testid="printer-card" tour="print-discovery" attrs={{ 'data-printer-count': printers.length }} title={t('print.printers')} tone={tone} open={open} setOpen={setOpen} summary={summary}
      actions={open && printers.length > 0 ? (
        <button type="button" id="add-printer-toggle" className={`bv-roundbtn bv-roundbtn--sm${adding ? ' is-on' : ''}`} title={adding ? t('common.close') : t('print.addRemove')} aria-label={adding ? t('common.close') : t('print.addRemove')} onClick={() => setAdding((a) => !a)}>
          <Icon name={adding ? 'x' : 'plus'} size={16} />
        </button>
      ) : null}>
      {pickerShown && (
        <div className="bv-pfield">
          <div className="bv-label">{t('print.computer')}</div>
          <Select className="bv-printpage__computer" value={device ? device.id : ''} aria-label={t('print.computer')} onChange={(e) => setDevice(e.target.value)}
            options={devices.map((d) => ({ value: d.id, label: `${d.name}${d.online ? '' : ` (${t('common.offline')})`}` }))} />
        </div>
      )}
      {helperState === 'otherAccount' && (
        <p className="bv-small bv-warntext" data-testid="helper-note">{t('print.helper.otherAccount')} {t('print.helper.otherAccountHow')}{device && online ? ` ${t('print.helper.via', { name: device.name })}` : ''}</p>
      )}
      {helperState === 'prompt' && <p className="bv-small bv-warntext" data-testid="helper-note">{t('print.helper.allowPrompt')}</p>}
      {helperState === 'blocked' && <p className="bv-small bv-warntext" data-testid="helper-note">{t('print.helper.blocked')}{device && online ? ` ${t('print.helper.via', { name: device.name })}` : ''}</p>}
      {device && online && !localDevice && helperState === 'found' && <p className="bv-small bv-muted" data-testid="helper-note">{t('print.helper.via', { name: device.name })}</p>}
      {device && !online && helperState === 'none' && (
        <p className="bv-small bv-warntext" data-testid="helper-note">{t('print.helper.openOrInstall')} <a href={helperDownloadUrl(browserOs())}>{t('print.helper.download', { os: browserOs() === 'mac' ? 'Mac' : 'Windows' })}</a></p>
      )}
      {device && !online && helperState === 'found' && <p className="bv-small bv-warntext" data-testid="helper-note">{t('print.helper.reconnecting')}</p>}
      {device && !online && helperState === 'checking' && <p className="bv-small bv-warntext" data-testid="helper-note">{t('print.deviceOffline', { name: device.name })}</p>}
      {showAdd && <AddForm online={online} finding={finding} onFind={async () => { const ok = await onFind(); if (ok) setAdding(false); }} onAddIp={async (ip) => { const ok = await onAddIp(ip); if (ok) setAdding(false); return ok; }}
        saved={printers} onRemove={onRemovePrinter} showSaved={adding} notify={notify} />}
      {printers.length > 0 && (
        <div className="bv-selection" id="selection">
          <div className="bv-label">{t('print.selectedPrinter')}</div>
          <div className="bv-selection__printer">
            {printer ? <span><b data-testid="selected-printer">{printer.model || printer.ip}</b><span className="bv-muted">{printer.ip}</span></span> : <span className="bv-muted">{t('print.selectPrinter')}</span>}
            <button type="button" id="printer-switch-btn" className="bv-roundbtn bv-roundbtn--sm" title={t('print.switchPrinter')} aria-label={t('print.switchPrinter')} onClick={() => setSwitching(true)}><Icon name="design" size={16} /></button>
          </div>
          {printer && (
            <>
              <div className="bv-label">{t('print.paperSource')}</div>
              <PaperPicker sources={sources} value={source} onChange={setSource} disabled={!sources.length || refreshing} loading={loadingInfo && !sources.length}
                placeholder={info ? t('print.selectSource') : t('print.waitingPrinter')} />
              {cur && paperTone(cur.status) === 'error' && <p className="bv-paperwarn" role="alert" data-testid="paper-warning">{t('print.noPaperIn', { name: cur.name || t('print.thisSource') })}</p>}
              {cur && !(parseFloat(cur.widthIn) > 0) && paperTone(cur.status) !== 'error' && <p className="bv-paperwarn" role="alert" data-testid="paper-warning">{t('print.noWidth', { name: cur.name || t('print.thisPaper') })}</p>}
              <button type="button" id="refresh-printer-btn" className="bv-refresh" disabled={loadingInfo || refreshing || !online} onClick={onRefresh}
                title={infoAt ? t('print.lastUpdated', { time: new Date(infoAt).toLocaleTimeString() }) : t('print.refreshInfo')}>
                <Icon name="refresh" size={16} />
                <span>{refreshing ? t('print.refreshing') : t('print.refreshPrinter')}</span>
                {infoAt && <span className="bv-refresh__ago"><span className={`bv-tdot bv-tdot--${freshTone(infoAt)}`} />{ago(infoAt)}</span>}
              </button>
              <LiveCard pill={pill} loading={loadingInfo} />
            </>
          )}
        </div>
      )}
      <SwitchModal open={switching} onClose={() => setSwitching(false)} printers={printers} selected={printer && printer.ip} onSelect={selectPrinter} onAddNew={() => setAdding(true)} />
    </PanelBox>
  );
}

/* ------------------------------------------------------------------ Print settings */
function SettingsOption({ id, title, status, on, disabledReason, open, onToggle, children }) {
  const [tip, setTip] = useState(false);
  return (
    <div className={`bv-sopt${disabledReason ? ' is-disabled' : ''}`} id={id}>
      <button type="button" className="bv-sopt__head" aria-expanded={!!open} disabled={!!disabledReason} onClick={onToggle}>
        <span className="bv-sopt__title">{title}</span>
        <span className={`bv-sopt__status${on ? ' is-on' : ''}`}>{status}</span>
        {!disabledReason && <Icon name="down" size={16} className="bv-sopt__chev" />}
      </button>
      {disabledReason && (
        <span className="bv-sopt__q" tabIndex={0} aria-label={disabledReason} onMouseEnter={() => setTip(true)} onMouseLeave={() => setTip(false)} onFocus={() => setTip(true)} onBlur={() => setTip(false)}>
          ?{tip && <span className="bv-sopt__tip" role="tooltip">{disabledReason}</span>}
        </span>
      )}
      {open && !disabledReason && <div className="bv-sopt__body">{children}</div>}
    </div>
  );
}

/** Numeric inch input that commits on valid keystrokes, on edited blur and on Enter (desktop InchInput). */
export function InchInput({ value, min, max, step, onCommit, disabled, className, label }) {
  const [draft, setDraft] = useState(null);
  const [focusText, setFocusText] = useState(null);
  const shown = draft != null ? draft : value != null && value !== '' ? Number(value).toFixed(2) : '';
  const commit = (raw, force) => { const v = parseFloat(raw); if (!Number.isFinite(v) || v <= 0) return; if (force || (v >= min && v <= max)) onCommit(v); };
  return (
    <input type="number" className={`bv-input ${className || ''}`} aria-label={label} value={shown} min={min} max={max} step={step} disabled={disabled}
      onFocus={(e) => { setFocusText(shown); setDraft(shown); e.target.select(); }}
      onChange={(e) => { setDraft(e.target.value); commit(e.target.value, false); }}
      onBlur={(e) => { if (e.target.value !== focusText) commit(e.target.value, true); setFocusText(null); setDraft(null); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }} />
  );
}

export const COPIES_MAX = 50;
export function PrintSettingsBox({ open, setOpen, copies, setCopies, nest, copiesOpen, setCopiesOpen, nestOpen, setNestOpen }) {
  const { t } = useTranslation('bv');
  const copiesTxt = t('print.copies', { count: copies });
  const nestStatus = nest.mode ? `${t('common.on')} · ${t('print.images', { count: nest.count })} · ${nest.fillPct}%` : nest.blocked ? t('print.settings.unavailable') : t('common.off');
  return (
    <PanelBox id="print-settings" testid="print-settings" title={t('print.settings.title')} open={open} setOpen={setOpen}
      summary={<span className="bv-small">{copies > 1 ? `${t('print.settings.copies')} ${copiesTxt} · ` : ''}{t('print.nest.nesting')} {nestStatus}</span>}>
      <SettingsOption id="copies-option" title={t('print.settings.copies')} status={copiesTxt} on={copies > 1} open={copiesOpen} onToggle={() => setCopiesOpen(!copiesOpen)}>
        <div className="bv-stepper" role="group" aria-label={t('print.settings.copiesAria')}>
          <button type="button" aria-label={t('print.settings.fewer')} disabled={copies <= 1} onClick={() => setCopies(copies - 1)}>−</button>
          <input id="copies-input" type="number" min={1} max={COPIES_MAX} value={copies} aria-label={t('print.settings.copiesAria')}
            onFocus={(e) => e.target.select()} onChange={(e) => setCopies(Math.max(1, Math.min(COPIES_MAX, Number(e.target.value) || 1)))} />
          <button type="button" aria-label={t('print.settings.more')} disabled={copies >= COPIES_MAX} onClick={() => setCopies(copies + 1)}>+</button>
        </div>
        <p className="bv-small bv-muted">{t('print.settings.copiesHint')}</p>
      </SettingsOption>
      <SettingsOption id="nest-option" title={t('print.nest.nesting')} status={nestStatus} on={nest.mode} disabledReason={!nest.mode ? nest.blocked : null} open={nestOpen} onToggle={() => setNestOpen(!nestOpen)}>
        {nest.mode ? <NestPanel nest={nest} copies={copies} /> : <p className="bv-small bv-muted">{t('print.nest.clickToStart')}</p>}
      </SettingsOption>
    </PanelBox>
  );
}

function NestPanel({ nest, copies }) {
  const { t } = useTranslation('bv');
  const sel = nest.selItem;
  const n = nest.printableCount;
  return (
    <div className="bv-nestbar" id="nest-bar" data-testid="nest-panel">
      <Button size="sm" onClick={nest.exit}>{t('print.nest.exit')}</Button>
      <p className="bv-small">
        {t('print.nest.dragHint')}
        {nest.unplaced > 0 && <> <button type="button" className="bv-linkbtn" onClick={nest.addAll}>{t('print.nest.addAll', { count: nest.unplaced })}</button></>}
      </p>
      {nest.count > 0 && (
        <div>
          <div className="bv-label">{t('print.nest.autoArrange')}</div>
          <div className="bv-seg">
            <button type="button" title={t('print.nest.equalTitle')} onClick={() => nest.arrange('equal')}>{t('print.nest.equal')}</button>
            <button type="button" title={t('print.nest.tightTitle')} onClick={() => nest.arrange('tight')}>{t('print.nest.tight')}</button>
          </div>
        </div>
      )}
      {sel ? (
        <div className="bv-nestsel" data-testid="nest-selected">
          <div className="bv-nestsel__head"><b>{t('print.nest.imageN', { n: sel.num })}</b><button type="button" className="bv-linkbtn" onClick={() => nest.remove(sel.id)}>{t('common.remove')}</button></div>
          <div className="bv-nestsel__size">
            <InchInput label={t('print.widthIn')} value={sel.w} min={1} max={120} step={0.25} onCommit={(v) => nest.setSize('w', v)} />
            <span>×</span>
            <InchInput label={t('print.heightIn')} value={sel.h} min={1} max={120} step={0.25} onCommit={(v) => nest.setSize('h', v)} />
          </div>
          {sel.cropped && <button type="button" className="bv-linkbtn" title={t('print.nest.uncropTitle')} onClick={nest.uncrop}>{t('print.nest.uncrop')}</button>}
          <div className="bv-seg">
            <button type="button" aria-pressed={sel.orientation === 'portrait'} className={sel.orientation === 'portrait' ? 'is-on' : ''} onClick={() => nest.setOrientation('portrait')}>{t('print.portrait')}</button>
            <button type="button" aria-pressed={sel.orientation === 'landscape'} className={sel.orientation === 'landscape' ? 'is-on' : ''} onClick={() => nest.setOrientation('landscape')}>{t('print.landscape')}</button>
          </div>
        </div>
      ) : <p className="bv-small bv-muted">{t('print.nest.selectHint')}</p>}
      <div className="bv-nestfoot">
        <div className="bv-small"><b>{`${t('print.images', { count: nest.count })} · ${nest.stripWidthIn.toFixed(1)}" × ${nest.stripHeightIn.toFixed(1)}"`}</b></div>
        <div className="bv-fill" title={t('print.nest.fillTitle')}>
          <div className="bv-fill__track"><div className="bv-fill__bar" style={{ width: `${nest.fillPct}%` }} /></div>
          <span>{t('print.nest.filled', { pct: nest.fillPct })}</span>
        </div>
        <Button variant="coral" block icon="print" disabled={!nest.canPrint || n === 0} onClick={nest.print} data-testid="nest-print">
          {`${t('print.print')} ${t('print.images', { count: n })}${copies > 1 ? ` ×${copies}` : ''}`}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Ink levels */
const ORDER = ['C', 'M', 'Y', 'B', 'CR', 'R', 'PB', 'MB', 'G', 'LG', 'LC', 'LM'];
const rank = (c) => { const i = ORDER.indexOf(c); return i < 0 ? 99 : i; };
function MiniRing({ pct, color }) {
  const r = 13; const c = 2 * Math.PI * r;
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
      <circle cx="17" cy="17" r={r} fill="none" stroke="var(--bv-fill)" strokeWidth="4" />
      <circle cx="17" cy="17" r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={`${(c * pct) / 100} ${c}`} transform="rotate(-90 17 17)" />
    </svg>
  );
}
export function InkBox({ open, setOpen, info, loading, hasPrinter, supplyUrl }) {
  const { t } = useTranslation('bv');
  const inks = ((info && info.ok && info.inks) || []).map(inkInfo).sort((a, b) => rank(a.code) - rank(b.code));
  const low = inks.filter((i) => i.percent < LOW_INK).sort((a, b) => a.percent - b.percent)[0];
  const lvl = (i) => (i.percent < CRITICAL_INK ? ' is-critical' : i.percent < LOW_INK ? ' is-low' : '');
  const title = (i) => `${i.name}: ${i.percent}%`;
  const empty = !hasPrinter ? t('print.ink.selectPrinter') : loading ? null : t('print.ink.notReported');
  return (
    <PanelBox id="inks" testid="ink-card" tour="print-inks" title={t('print.ink.title')} open={open} setOpen={setOpen}
      summary={inks.length ? (
        <div className="bv-inkstrip" id="ink-compact">
          {inks.map((i, n) => (
            <div key={n} className={`bv-inkbar${lvl(i)}`} title={title(i)}>
              <div className="bv-inkbar__track"><div className="bv-inkbar__fill" style={{ height: `${i.percent}%`, background: i.color }} /></div>
              <span>{i.code}</span>
            </div>
          ))}
        </div>
      ) : <span className="bv-small bv-muted">{empty || t('print.ink.reading')}</span>}>
      {inks.length ? (
        <div className="bv-inkrow" style={{ gridTemplateColumns: `repeat(${inks.length}, minmax(0, 1fr))` }} data-testid="ink-row">
          {inks.map((i, n) => (
            <div key={n} className={`bv-inktile${lvl(i)}`} title={title(i)}>
              <span className="bv-inktile__code" style={{ color: i.color === '#FFD600' ? '#C9A400' : i.color }}>{i.code}</span>
              <MiniRing pct={i.percent} color={i.color} />
              <span className="bv-inktile__pct">{i.percent}%</span>
              {i.percent < LOW_INK && <span className="bv-inktile__warn" aria-label={i.percent < CRITICAL_INK ? t('print.ink.nearlyEmpty') : t('print.ink.low')}>{i.percent < CRITICAL_INK ? '!' : '▲'}</span>}
            </div>
          ))}
        </div>
      ) : loading ? <div className="bv-inkrow" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}>{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="bv-inktile is-loading"><span>…</span><span className="bv-inktile__ring" /><span>⏳</span></div>)}</div>
        : <p className="bv-small bv-muted">{empty}</p>}
      {low && (
        <div className="bv-lowink" role="status">
          <span>{t('print.ink.runningLow', { name: low.name.replace(/^\w/, (c) => c.toUpperCase()) })}</span>
          <a href={supplyUrl} target="_blank" rel="noreferrer">{t('print.ink.order')}</a>
        </div>
      )}
    </PanelBox>
  );
}

/* ------------------------------------------------------------------ Print jobs (approval-required teachers) */
const SORTS = [['newest', 'print.jobs.sort.newest'], ['oldest', 'print.jobs.sort.oldest'], ['pending', 'print.jobs.sort.pending'], ['rejected', 'print.jobs.sort.rejected'], ['approved', 'print.jobs.sort.approved']];
const REQ = { pending: ['status.pending', 'yellow'], approved: ['status.approved', 'green'], failed: ['status.failed', 'coral'], printed: ['status.printed', 'green'], rejected: ['status.rejected', 'coral'], cancelled: ['status.cancelled', 'idle'], expired: ['status.expired', 'idle'] };
const reqOf = (j) => (j.status === 'approved' && j.print_failed ? 'failed' : j.status);
const since = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return i18n.t('bv:time.justNowLower');
  if (s < 3600) return i18n.t('bv:time.minAgo', { count: Math.round(s / 60) });
  if (s < 86400) return i18n.t('bv:time.hoursAgo', { count: Math.round(s / 3600) });
  return i18n.t('bv:time.daysAgo', { count: Math.round(s / 86400) });
};
export function PrintJobsBox({ open, setOpen, version }) {
  const { t } = useTranslation('bv');
  const [jobs, setJobs] = useState(null);
  const [sort, setSort] = useState(() => store.get('pp.printJobsSort') || 'newest');
  const [pos, setPos] = useState(0);
  const [reason, setReason] = useState(null);
  const load = useCallback(() => listMine().then((r) => setJobs(r.ok ? r.jobs || [] : [])), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load, version]);
  const list = [...(jobs || [])].sort((a, b) => {
    const t = new Date(b.created_at) - new Date(a.created_at);
    if (sort === 'oldest') return -t;
    if (['pending', 'rejected', 'approved'].includes(sort)) { const d = (reqOf(b) === sort) - (reqOf(a) === sort); return d || t; }
    return t;
  });
  const j = list[Math.min(pos, Math.max(0, list.length - 1))];
  const counts = (jobs || []).reduce((m, x) => { const k = reqOf(x); m[k] = (m[k] || 0) + 1; return m; }, {});
  const tone = counts.rejected ? 'error' : counts.pending ? 'warn' : 'ok';
  const step = (d) => setPos((p) => Math.max(0, Math.min(list.length - 1, p + d)));
  const cycle = (d) => { const i = (SORTS.findIndex((s) => s[0] === sort) + d + SORTS.length) % SORTS.length; setSort(SORTS[i][0]); store.set('pp.printJobsSort', SORTS[i][0]); setPos(0); };
  const act = async (fn) => { await fn(); load(); };
  return (
    <PanelBox id="print-jobs" testid="print-jobs" title={t('print.jobs.title')} tone={jobs && jobs.length ? tone : null} open={open} setOpen={setOpen}
      summary={jobs && jobs.length ? <span className="bv-small">{[counts.pending && t('print.jobs.nPending', { count: counts.pending }), counts.approved && t('print.jobs.nApproved', { count: counts.approved }), counts.rejected && t('print.jobs.nNotPrinted', { count: counts.rejected })].filter(Boolean).join(' · ') || t('print.jobs.closed', { count: jobs.length })}</span> : <span className="bv-small bv-muted">{t('print.jobs.hint')}</span>}>
      {!jobs ? <Spinner /> : !jobs.length ? <p className="bv-small bv-muted">{t('print.jobs.hint')}</p> : (
        <>
          <div className="bv-jobsort"><button type="button" aria-label={t('print.jobs.prevOrder')} onClick={() => cycle(-1)}>‹</button><span>{t(SORTS.find((s) => s[0] === sort)[1])}</span><button type="button" aria-label={t('print.jobs.nextOrder')} onClick={() => cycle(1)}>›</button></div>
          <div className="bv-jobview" onWheel={(e) => step(e.deltaY > 0 ? 1 : -1)}>
            {j && (
              <div className="bv-jobview__card">
                <span className="bv-jobview__thumb">{j.thumbUrl ? <img src={j.thumbUrl} alt="" /> : <Icon name="image" size={20} />}</span>
                <div className="bv-jobview__body">
                  <b>{j.design_name || t('common.untitledDesign')}</b>
                  <span className={`bv-reqpill bv-reqpill--${(REQ[reqOf(j)] || REQ.pending)[1]}`}>{REQ[reqOf(j)] ? t(REQ[reqOf(j)][0]) : reqOf(j)}</span>
                  {reqOf(j) === 'rejected' && j.decision_note && <button type="button" className="bv-linkbtn" onClick={() => setReason(j.decision_note)}>{t('print.jobs.reason')} {j.decision_note.slice(0, 40)}</button>}
                  <span className="bv-small bv-muted" title={t('print.jobs.sent', { when: since(j.created_at) })}>{since(j.created_at)}</span>
                </div>
                {reqOf(j) === 'pending'
                  ? <Button size="sm" title={t('print.jobs.cancelTitle')} onClick={() => act(() => cancelJob(j.id))}>{t('common.cancel')}</Button>
                  : <Button size="sm" title={t('print.jobs.removeTitle')} onClick={() => act(() => dismissJob(j.id))}>{t('common.remove')}</Button>}
              </div>
            )}
            <div className="bv-jobview__nav">
              <button type="button" aria-label={t('print.jobs.prev')} title={t('print.jobs.prev')} disabled={pos <= 0} onClick={() => step(-1)}>˄</button>
              <span>{`${Math.min(pos, list.length - 1) + 1} / ${list.length}`}</span>
              <button type="button" aria-label={t('print.jobs.next')} title={t('print.jobs.next')} disabled={pos >= list.length - 1} onClick={() => step(1)}>˅</button>
            </div>
          </div>
        </>
      )}
      <Modal open={!!reason} title={t('print.jobs.reasonTitle')} onClose={() => setReason(null)}><p>{reason}</p></Modal>
    </PanelBox>
  );
}
